import { formatDisplayShort, parseDateSafe } from '../../../lib/dateUtils';
import { getElementTypeLabel } from '../../../constants/AssetTestConfig';
import { useAuthStore } from '../../../store/authStore';
import { isOwnInProgressSession } from '../../../lib/sessionOwnership';
import SiteStatusBadge from './SiteStatusBadge';

function formatLastOpened(value) {
  const d = parseDateSafe(value);
  if (!d) return '—';
  return formatDisplayShort(d);
}

/**
 * Desktop sidebar for the Sites page. Shows org-wide summary counts when no row
 * is hovered, and a per-site preview (status, next-test date, asset breakdown,
 * active sessions) when a row is hovered. Display-only; all data is computed by
 * the parent and passed in.
 */
export default function SiteSummaryPanel({
  hoveredSite,
  summaryCounts,
  assetsSummary,
  hoveredDefectMeta,
  hoveredAssetsSummary,
  hoveredSessions,
}) {
  const user = useAuthStore((s) => s.user);

  return (
    <aside className="sites-desktop-aside" aria-label="Sites summary">
      <div className="sites-desktop-panel">
        {!hoveredSite ? (
          <>
            <h2 className="sites-desktop-panel-title">Sites summary</h2>
            <div className="sites-desktop-metric-row">
              <span className="sites-desktop-metric-label">Total sites</span>
              <span className="sites-desktop-metric-value">{summaryCounts.total}</span>
            </div>
            <div className="sites-desktop-metric-row">
              <span className="sites-desktop-metric-label">Compliant</span>
              <span className="sites-desktop-metric-value">{summaryCounts.isLoading ? '…' : summaryCounts.compliant}</span>
            </div>
            <div className="sites-desktop-metric-row">
              <span className="sites-desktop-metric-label">Repairs due</span>
              <span className="sites-desktop-metric-value">{summaryCounts.isLoading ? '…' : summaryCounts.repairsDue}</span>
            </div>
            <div className="sites-desktop-preview-meta">
              <div className="sites-desktop-preview-section-title" style={{ marginBottom: '0.6rem' }}>Assets</div>
              <div className="sites-desktop-metric-row" style={{ borderBottom: 'none', paddingBottom: 0 }}>
                <span className="sites-desktop-metric-label">Total</span>
                <span className="sites-desktop-metric-value">{assetsSummary.isLoading ? '…' : assetsSummary.total}</span>
              </div>
              {assetsSummary.breakdown.map((row) => (
                <div key={row.key} className="sites-desktop-metric-row" style={{ borderBottom: 'none', paddingTop: '0.25rem', paddingBottom: '0.25rem' }}>
                  <span className="sites-desktop-metric-label">{row.label}</span>
                  <span className="sites-desktop-metric-value">{assetsSummary.isLoading ? '…' : row.count}</span>
                </div>
              ))}
              {assetsSummary.isError ? (
                <p className="text-muted" style={{ marginTop: '0.5rem' }}>Assets summary unavailable.</p>
              ) : null}
            </div>
            <p className="text-muted" style={{ marginTop: '0.75rem' }}>
              Hover a site row to preview details.
            </p>
          </>
        ) : (
          <>
            <h2 className="sites-desktop-panel-title">
              {hoveredSite.display_name ?? hoveredSite.name?.trim() ?? hoveredSite.site_code ?? 'Unnamed site'}
            </h2>
            <p className="text-muted" style={{ marginTop: '-0.25rem' }}>
              {[hoveredSite.city, hoveredSite.state].filter(Boolean).join(', ') || hoveredSite.address || '—'}
            </p>
            <div style={{ marginTop: '0.5rem' }}>
              <SiteStatusBadge
                count={hoveredDefectMeta?.count ?? 0}
                isLoading={hoveredDefectMeta?.isLoading ?? true}
                isError={hoveredDefectMeta?.isError ?? false}
              />
            </div>
            <div className="sites-desktop-preview-meta">
              <div className="sites-desktop-preview-meta-row">
                <span className="sites-desktop-preview-meta-label">Next test due</span>
                <span className="sites-desktop-preview-meta-value">
                  {hoveredSite.next_test_due_date ? formatDisplayShort(hoveredSite.next_test_due_date) : '—'}
                </span>
              </div>
            </div>

            <div className="sites-desktop-preview-meta">
              <div className="sites-desktop-preview-section-title" style={{ marginBottom: '0.6rem' }}>Assets</div>
              <div className="sites-desktop-metric-row" style={{ borderBottom: 'none', paddingBottom: 0 }}>
                <span className="sites-desktop-metric-label">Total</span>
                <span className="sites-desktop-metric-value">{hoveredAssetsSummary.isLoading ? '…' : hoveredAssetsSummary.total}</span>
              </div>
              {hoveredAssetsSummary.breakdown.map((row) => (
                <div key={row.key} className="sites-desktop-metric-row" style={{ borderBottom: 'none', paddingTop: '0.25rem', paddingBottom: '0.25rem' }}>
                  <span className="sites-desktop-metric-label">{row.label}</span>
                  <span className="sites-desktop-metric-value">{hoveredAssetsSummary.isLoading ? '…' : row.count}</span>
                </div>
              ))}
              {hoveredAssetsSummary.isError ? (
                <p className="text-muted" style={{ marginTop: '0.5rem' }}>Assets summary unavailable.</p>
              ) : null}
            </div>

            <div className="sites-desktop-preview-section">
              <div className="sites-desktop-preview-section-title">Active sessions</div>
              <div className="sites-desktop-preview-meta-row" style={{ marginBottom: '0.5rem' }}>
                <span className="sites-desktop-preview-meta-label">Repairs required</span>
                <span className="sites-desktop-preview-meta-value">
                  {hoveredDefectMeta?.isLoading ? '…' : (hoveredDefectMeta?.isError ? '—' : (hoveredDefectMeta?.count ?? 0))}
                </span>
              </div>
              {hoveredSessions.length > 0 ? (
                <ul className="sites-desktop-session-list">
                  {hoveredSessions.map((s) => (
                    <li key={s.id} className="sites-desktop-session-item">
                      <span className="sites-desktop-session-type">{getElementTypeLabel(s.element_type)}</span>
                      <span className="sites-desktop-session-meta">
                        {isOwnInProgressSession(s, user) ? 'Yours' : `Started by ${s.user_name || 'Unknown'}`}
                        {' · '}
                        Last opened: {formatLastOpened(s.start_time)}
                      </span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-muted">No active sessions.</p>
              )}
            </div>
          </>
        )}
      </div>
    </aside>
  );
}
